import React, { useState } from 'react';
import { Container, Typography, Button, Box, Paper } from '@mui/material';
import Grid from '@mui/material/Grid';
import { ArrowRight, CheckCircle } from 'lucide-react';
import AuthModal from './auth/AuthModal';
import { useAuth } from '../contexts/AuthContext';

const Hero: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const { isAuthenticated, redirectToPartnerSSO } = useAuth();

  const handleGetStartedClick = async () => {
    if (isAuthenticated) {
      // If user is authenticated, redirect to partner platform
      try {
        await redirectToPartnerSSO();
      } catch (error) {
        console.error('Failed to redirect to partner platform:', error);
      }
    } else {
      // If not authenticated, show auth modal
      setModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  return (
    <>
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
          color: 'white',
          pt: { xs: 6, md: 10 },
          pb: { xs: 6, md: 10 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >   
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center" justifyContent="center">
            {/* Video First */}
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={16}
                sx={{ 
                  borderRadius: 4,
                  overflow: 'hidden',
                  transform: { md: 'perspective(1000px) rotateY(0deg) rotateX(0deg)' },
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: { md: 'perspective(1000px) rotateY(-2deg) rotateX(-2deg)' },
                  },
                  boxShadow: '0 20px 80px 0 rgba(0, 0, 0, 0.3)',
                }}
              >
                <Box
                  component="video"
                  autoPlay
                  muted
                  playsInline
                  sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                >
                  <source src="/assets/RIC_Animation8_1080p.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </Box>
              </Paper>
            </Grid>

            {/* Text Second */}
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 4, 
                  fontWeight: 400,
                  opacity: 0.9,
                  maxWidth: '90%',
                }}
              >
                Transform your hiring process with our advanced AI talent search and scoring technology
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckCircle size={20} className="text-secondary-main" />
                  <Typography>Hire 80% <strong>faster</strong></Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckCircle size={20} className="text-secondary-main" />
                  <Typography>Access 100% of both <strong>active</strong> and <strong>passive</strong> candidates</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <CheckCircle size={20} className="text-secondary-main" style={{marginTop: '4px'}} />
                  <Typography>
                    The only model developed and backed by an <strong>award-winning,<br/> Forbes ranked professional recruitment firm</strong>
                    <br/>
                    <Box 
                      component="span"
                      sx={{ 
                        color: '#002588', 
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                      onClick={() => window.open('https://arcgonline.com', '_blank', 'noopener,noreferrer')}
                    >
                      (American Recruiting & Consulting Group)
                    </Box>
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  size="large"
                  onClick={handleGetStartedClick}
                  endIcon={<ArrowRight size={18} />}
                  sx={{ 
                    py: 1.5,
                    boxShadow: '0 4px 14px 0 rgba(20, 20,20, 0.39)',
                  }}
                >
                  {isAuthenticated ? 'Access Platform' : 'Get Started'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <AuthModal
        open={modalOpen}
        onClose={handleModalClose}
        initialView="signup"
      />
    </>
  );
};

export default Hero;